package com.ijoin.ui.tickets

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.model.Ticket
import com.ijoin.data.model.TicketStatus
import com.ijoin.data.repository.TicketFetchResult
import com.ijoin.data.repository.TicketRepository
import com.ijoin.i18n.Strings
import com.ijoin.i18n.globalStrings
import com.ijoin.platform.currentInstant
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant

data class TicketsUiState(
    val isLoading: Boolean = false,
    val isOffline: Boolean = false,
    val tickets: List<Ticket> = emptyList(),
    val upcomingPaidTickets: List<Ticket> = emptyList(),
    val errorMessage: String? = null
)

class TicketsViewModel(
    private val ticketRepository: TicketRepository = TicketRepository()
) : ViewModel() {

    private val _uiState = MutableStateFlow(TicketsUiState())
    val uiState: StateFlow<TicketsUiState> = _uiState.asStateFlow()
    private val strings: Strings
        get() = globalStrings()

    private var hasLoaded = false

    fun loadTickets(forceRefresh: Boolean = false) {
        if (_uiState.value.isLoading) return
        if (!forceRefresh && hasLoaded) return

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }

            // Show cached tickets immediately if available and we are not forcing a refresh
            if (!forceRefresh) {
                val cachedTickets = ticketRepository.getCachedTickets()
                if (cachedTickets.isNotEmpty()) {
                    updateStateWithTickets(cachedTickets, fromCache = false, keepLoading = true)
                }
            }

            val fetchResult = ticketRepository.fetchUserTickets()
            handleFetchResult(fetchResult)
            hasLoaded = true
        }
    }

    fun refresh() {
        hasLoaded = false
        loadTickets(forceRefresh = true)
    }

    private fun handleFetchResult(result: TicketFetchResult) {
        if (result.tickets.isNotEmpty()) {
            updateStateWithTickets(
                tickets = result.tickets,
                fromCache = result.fromCache,
                errorMessage = result.error?.message
            )
        } else {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    tickets = emptyList(),
                    upcomingPaidTickets = emptyList(),
                    isOffline = false,
                    errorMessage = result.error?.message
                )
            }
        }
    }

    private fun updateStateWithTickets(
        tickets: List<Ticket>,
        fromCache: Boolean,
        keepLoading: Boolean = false,
        errorMessage: String? = null
    ) {
        val upcoming = computeUpcomingPaidTickets(tickets)
        _uiState.update {
            it.copy(
                isLoading = keepLoading,
                tickets = tickets,
                upcomingPaidTickets = upcoming,
                isOffline = fromCache,
                errorMessage = errorMessage?.takeIf { fromCache } // only surface message when offline
            )
        }
        if (!keepLoading) {
            _uiState.update { current -> current.copy(isLoading = false) }
        }
    }

    private fun computeUpcomingPaidTickets(tickets: List<Ticket>): List<Ticket> {
        val now = currentInstant()
        return tickets.mapNotNull { ticket ->
            val dateTime = ticket.resolveEventDateTime() ?: return@mapNotNull null
            if (ticket.status == TicketStatus.ACTIVE && ticket.paidPrice != null && dateTime >= now) {
                ticket to dateTime
            } else {
                null
            }
        }.sortedBy { it.second }
            .map { it.first }
    }

    private fun Ticket.resolveEventDateTime(): Instant? {
        return eventDate ?: occurrenceDateTime ?: createdAt
    }
}
